import { FC } from 'react';
import { createUseStyles } from 'react-jss';
import dayjs from 'dayjs';
import { useInstance } from '@meraki-internal/react-dependency-injection';
import { useSubscription } from '@meraki-internal/state';
import { CommunityModelFactory } from '../../share-caremap/CommunityModelFactory';
import { Spinner } from '../../components/Spinner';
import { IAction, Menu } from '../../components/Menu';
import { DeleteIcon } from '../../theme/Icons';
import { getInitials, IPersonRow } from './prototypeData';

// ─────────────────────────────────────────────────────────────────────────────
// IH-6912 spike — unified people list.
//
// PeopleList is the pure renderer: takes rows and renders them with the
// 3-dot menu. Mock data (brand-new / full presets) and real data (populated
// preset) both adapt to IPersonRow so the row UI is identical across sources.
//
// RealHivePeopleList is a thin wrapper that loads real data via
// CommunityModelFactory (the same source as the production CareMapCollaboratorList)
// and renders PeopleList.
// ─────────────────────────────────────────────────────────────────────────────

const useStyles = createUseStyles({
    list: {
        display: 'flex',
        flexDirection: 'column'
    },
    row: {
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        padding: '10px 0',
        borderBottom: '1px solid var(--ion-color-step-100)'
    },
    avatar: {
        width: 36,
        height: 36,
        borderRadius: '50%',
        background: 'var(--ion-color-step-100)',
        color: 'var(--ion-color-primary)',
        fontSize: 13,
        fontWeight: 700,
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0
    },
    main: {
        flex: 1,
        minWidth: 0
    },
    name: {
        fontSize: 14,
        fontWeight: 600,
        color: 'var(--ion-text-color)'
    },
    email: {
        fontSize: 12,
        color: 'var(--ion-color-step-750)',
        wordBreak: 'break-all'
    },
    status: {
        fontSize: 11,
        color: 'var(--ion-color-medium)',
        marginTop: 4,
        display: 'flex',
        gap: 6,
        alignItems: 'center',
        flexWrap: 'wrap'
    },
    rolePill: {
        fontSize: 11,
        fontWeight: 600,
        padding: '2px 7px',
        borderRadius: 999
    },
    pillCo: {
        background: 'rgba(146,61,172,0.12)',
        color: 'var(--ion-color-tertiary)'
    },
    pillView: {
        background: 'rgba(17,120,157,0.10)',
        color: 'var(--ion-color-primary)'
    },
    pillOwner: {
        background: 'var(--ion-color-step-100)',
        color: 'var(--ion-color-step-750)'
    },
    relationshipTag: {
        fontSize: 11,
        fontWeight: 600,
        padding: '2px 7px',
        borderRadius: 999,
        background: 'var(--ion-color-step-50)',
        color: 'var(--ion-color-step-750)',
        border: '1px solid var(--ion-color-step-100)'
    },
    statusSep: {
        color: 'var(--ion-color-step-400)'
    },
    menuSlot: {
        marginLeft: 4
    },
    loadingWrap: {
        minHeight: 120,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
    }
});

const pillClassFor = (classes: ReturnType<typeof useStyles>, role: IPersonRow['role']): string => {
    if (role === 'Co-Owner') {
        return `${classes.rolePill} ${classes.pillCo}`;
    }
    if (role === 'Viewer') {
        return `${classes.rolePill} ${classes.pillView}`;
    }
    return `${classes.rolePill} ${classes.pillOwner}`;
};

export interface IPeopleListProps {
    rows: IPersonRow[];
    /** Called for menu actions that are stubbed in the spike (resend, switch role). */
    onStubAction: (message: string) => void;
    /** Called by Remove Access. Resolves true when the user confirms. */
    confirmRemove: (label: string) => Promise<boolean>;
}

export const PeopleList: FC<IPeopleListProps> = ({ rows, onStubAction, confirmRemove }) => {
    const classes = useStyles();

    const buildActions = (row: IPersonRow): IAction[] => {
        const switchToLabel = row.role === 'Co-Owner' ? 'Switch to Viewer' : 'Switch to Co-Owner';
        return [
            {
                text: 'Resend Invite',
                hidden: !row.isPending,
                'data-type': 'resend-invite',
                handler: () => onStubAction(`Stub: would resend ${row.name}'s invite.`)
            },
            {
                text: 'Update in Care Map',
                hidden: !row.onUpdateInCareMap,
                'data-type': 'update-care-map',
                handler: () => row.onUpdateInCareMap?.()
            },
            {
                text: switchToLabel,
                'data-type': 'switch-access',
                handler: () => onStubAction(`Stub: would ${switchToLabel.toLowerCase()} for ${row.name}.`)
            },
            {
                text: 'Remove Access',
                role: 'destructive',
                Icon: DeleteIcon,
                'data-type': 'remove-access',
                disabled: !row.onRemove,
                handler: async () => {
                    if (!row.onRemove) {
                        return;
                    }
                    const confirmed = await confirmRemove(row.name);
                    if (confirmed) {
                        await row.onRemove();
                    }
                }
            }
        ];
    };

    return (
        <div className={classes.list}>
            {rows.map(row => (
                <div key={row.key} className={classes.row}>
                    <div className={classes.avatar}>{row.initials || '?'}</div>
                    <div className={classes.main}>
                        <div className={classes.name}>{row.name}</div>
                        {row.email && <div className={classes.email}>{row.email}</div>}
                        <div className={classes.status}>
                            <span className={pillClassFor(classes, row.role)}>{row.role}</span>
                            {row.careMapRole && (
                                <span className={classes.relationshipTag}>{row.careMapRole}</span>
                            )}
                            <span className={classes.statusSep}>·</span>
                            <span>{row.statusLine}</span>
                        </div>
                    </div>
                    {!row.isOwner && (
                        <div className={classes.menuSlot}>
                            <Menu actions={buildActions(row)} data-id={`row-menu-${row.key}`} />
                        </div>
                    )}
                </div>
            ))}
        </div>
    );
};

const formatDate = (iso?: string): string => {
    if (!iso) {
        return '';
    }
    return dayjs(iso).format('M/D/YY');
};

export interface IRealHivePeopleListProps {
    onStubAction: (message: string) => void;
    confirmRemove: (label: string) => Promise<boolean>;
}

/**
 * Loads real owner/co-owners/viewers/pending invites from CommunityModelFactory
 * (same source as the production CareMapCollaboratorList), adapts them to
 * IPersonRow, and renders PeopleList.
 */
export const RealHivePeopleList: FC<IRealHivePeopleListProps> = ({ onStubAction, confirmRemove }) => {
    const classes = useStyles();

    const factory = useInstance(CommunityModelFactory);
    useSubscription(() => factory);
    const community = factory.forActiveCaremap();

    if (!community.isLoaded()) {
        return (
            <div className={classes.loadingWrap}>
                <Spinner />
            </div>
        );
    }

    const rows: IPersonRow[] = [];

    const owner = community.getOwner();
    if (owner) {
        rows.push({
            key: 'owner',
            name: owner.name || '(no name)',
            email: owner.email || '',
            initials: getInitials(owner.name || '', ''),
            role: 'Owner',
            isOwner: true,
            isPending: false,
            statusLine: 'Innerhive owner'
        });
    }

    for (const c of community.getPermanentCoOwners()) {
        rows.push({
            key: `co-${c.invite.token}`,
            name: c.invite.intendedRecipient.name || '(no name)',
            email: c.invite.acceptedBy.email || '',
            initials: getInitials(c.invite.intendedRecipient.name || '', ''),
            role: 'Co-Owner',
            isOwner: false,
            isPending: false,
            statusLine: `Joined ${formatDate(c.consentedAt)}`,
            onRemove: async () => {
                await community.deleteCollaborator(c.invite.token);
            }
        });
    }

    for (const v of community.getPermanentViewers()) {
        rows.push({
            key: `vw-${v.invite.token}`,
            name: v.invite.intendedRecipient.name || '(no name)',
            email: v.invite.acceptedBy.email || '',
            initials: getInitials(v.invite.intendedRecipient.name || '', ''),
            role: 'Viewer',
            isOwner: false,
            isPending: false,
            statusLine: `Joined ${formatDate(v.consentedAt)}`,
            onRemove: async () => {
                await community.deleteCollaborator(v.invite.token);
            }
        });
    }

    const pendingInvites = [
        ...community.getInvites('co-owner'),
        ...community.getInvites('view-only')
    ].sort((a, b) => dayjs(a.createdAt).isAfter(b.createdAt) ? -1 : 1);

    for (const inv of pendingInvites) {
        const isCo = inv.access === 'co-owner';
        rows.push({
            key: `inv-${inv.token}`,
            name: inv.intendedRecipient.name || '(no name)',
            email: inv.intendedRecipient.email || '',
            initials: getInitials(inv.intendedRecipient.name || '', ''),
            role: isCo ? 'Co-Owner' : 'Viewer',
            isOwner: false,
            isPending: true,
            statusLine: `Invited ${formatDate(inv.createdAt)}`,
            onRemove: inv.links.self
                ? async () => { await community.deleteInvite(inv.token); }
                : undefined
        });
    }

    return <PeopleList rows={rows} onStubAction={onStubAction} confirmRemove={confirmRemove} />;
};
